import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Upload,
  Link,
  Image as ImageIcon,
  Clock,
  Loader2,
  Check,
  AlertCircle
} from 'lucide-react';
import { reportImagesAPI } from '../../api/clientReports';

const ImageBlockPicker = ({ isOpen, onClose, onSelect }) => {
  const [activeTab, setActiveTab] = useState('upload'); // 'upload', 'url', 'recent'
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [blockType, setBlockType] = useState('inline'); // 'header', 'divider', 'inline'
  const [caption, setCaption] = useState('');
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch recent images
  const { data: recentImages, isLoading: loadingRecent } = useQuery({
    queryKey: ['recent-report-images'],
    queryFn: () => reportImagesAPI.listRecent(20).then(r => r.data.images),
    enabled: isOpen && activeTab === 'recent'
  });

  if (!isOpen) return null;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target.result;
        setPreviewUrl(base64Data);

        // Upload to server
        const response = await reportImagesAPI.upload({
          filename: file.name,
          mimeType: file.type,
          data: base64Data
        });

        setUploading(false);

        if (response.data.type === 'uploaded') {
          // Successfully uploaded, show preview and allow selection
          setPreviewUrl(reportImagesAPI.getImageUrl(response.data.id));
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to upload image');
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect({ target: { files: [file] } });
    }
  };

  const handleUrlSubmit = () => {
    if (!imageUrl.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(imageUrl);
      setPreviewUrl(imageUrl);
      setError('');
    } catch {
      setError('Please enter a valid URL');
    }
  };

  const handleSelectImage = () => {
    if (!previewUrl) {
      setError('Please select or upload an image first');
      return;
    }

    onSelect({
      type: blockType,
      src: previewUrl,
      caption: caption.trim() || null,
      width: blockType === 'header' ? 'full' : blockType === 'divider' ? 'full' : 'medium'
    });

    // Reset and close
    setPreviewUrl('');
    setCaption('');
    setBlockType('inline');
    setImageUrl('');
    onClose();
  };

  const handleSelectRecent = (image) => {
    setPreviewUrl(reportImagesAPI.getImageUrl(image.id));
    setActiveTab('upload'); // Switch to upload tab to show preview
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add Image Block</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-200">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('upload')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'upload'
                  ? 'border-sc-pink text-sc-pink'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Upload
            </button>
            <button
              onClick={() => setActiveTab('url')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'url'
                  ? 'border-sc-pink text-sc-pink'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Link className="w-4 h-4 inline mr-2" />
              From URL
            </button>
            <button
              onClick={() => setActiveTab('recent')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'recent'
                  ? 'border-sc-pink text-sc-pink'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="w-4 h-4 inline mr-2" />
              Recent
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="space-y-6">
              {/* Upload Area */}
              {!previewUrl && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-sc-pink bg-sc-pink/10'
                      : 'border-gray-300 hover:border-sc-pink hover:bg-sc-pink/10'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-10 h-10 text-sc-pink animate-spin mb-3" />
                      <p className="text-gray-600">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="w-10 h-10 text-gray-400 mb-3" />
                      <p className="text-gray-600 mb-1">Click to upload or drag and drop</p>
                      <p className="text-sm text-gray-400">PNG, JPG, GIF, WebP up to 5MB</p>
                    </div>
                  )}
                </div>
              )}

              {/* Preview */}
              {previewUrl && (
                <div className="space-y-4">
                  <div className="relative rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-auto max-h-64 object-contain mx-auto"
                    />
                    <button
                      onClick={() => setPreviewUrl('')}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* URL Tab */}
          {activeTab === 'url' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL
                </label>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.png"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
                  />
                  <button
                    onClick={handleUrlSubmit}
                    className="px-4 py-2 bg-sc-pink text-white rounded-lg hover:bg-sc-pink/90 transition-colors"
                  >
                    Load
                  </button>
                </div>
              </div>

              {/* Preview */}
              {previewUrl && (
                <div className="relative rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-auto max-h-64 object-contain mx-auto"
                    onError={() => setError('Failed to load image from URL')}
                  />
                  <button
                    onClick={() => {
                      setPreviewUrl('');
                      setImageUrl('');
                    }}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Recent Tab */}
          {activeTab === 'recent' && (
            <div>
              {loadingRecent ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-sc-pink animate-spin" />
                </div>
              ) : recentImages && recentImages.length > 0 ? (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                  {recentImages.map((image) => (
                    <button
                      key={image.id}
                      onClick={() => handleSelectRecent(image)}
                      className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 hover:ring-2 hover:ring-sc-pink transition-all group"
                    >
                      <img
                        src={reportImagesAPI.getImageUrl(image.id)}
                        alt={image.filename}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-colors flex items-center justify-center">
                        <Check className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No recent images</p>
                </div>
              )}
            </div>
          )}

          {/* Block Type & Caption (shown when image selected) */}
          {previewUrl && (
            <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
              {/* Block Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Block Type
                </label>
                <div className="flex space-x-3">
                  {[
                    { value: 'header', label: 'Header Banner', desc: 'Full-width at top of section' },
                    { value: 'divider', label: 'Section Divider', desc: 'Full-width between sections' },
                    { value: 'inline', label: 'Inline Image', desc: 'Within content area' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setBlockType(type.value)}
                      className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors ${
                        blockType === type.value
                          ? 'border-sc-pink bg-sc-pink/10'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className={`font-medium text-sm ${
                        blockType === type.value ? 'text-sc-pink' : 'text-gray-700'
                      }`}>
                        {type.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Caption */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Caption (optional)
                </label>
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Add a caption for this image..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSelectImage}
            disabled={!previewUrl}
            className="px-4 py-2 bg-sc-pink text-white rounded-lg hover:bg-sc-pink/90 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Insert Image
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageBlockPicker;
